import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { COSMIC_MOTION_EVENT, isCosmicDeviceMotionEnabled } from "@/lib/cosmicMotion";

const STAR_VERTEX_SHADER = `
  uniform float uTime;
  uniform float uPixelRatio;

  attribute float aSize;
  attribute float aOpacity;
  attribute float aPhase;
  attribute vec3 color;

  varying float vOpacity;
  varying vec3 vColor;

  void main() {
    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
    float twinkle = 0.9 + sin(uTime * 0.72 + aPhase) * 0.1;
    float perspectiveSize = aSize * uPixelRatio * (50.0 / max(1.0, -viewPosition.z));

    gl_Position = projectionMatrix * viewPosition;
    gl_PointSize = clamp(perspectiveSize * twinkle, 0.8, 7.5);
    vOpacity = aOpacity;
    vColor = color;
  }
`;

const STAR_FRAGMENT_SHADER = `
  varying float vOpacity;
  varying vec3 vColor;

  void main() {
    vec2 point = gl_PointCoord - vec2(0.5);
    float distanceToCenter = length(point);

    if (distanceToCenter > 0.5) discard;

    float core = 1.0 - smoothstep(0.0, 0.19, distanceToCenter);
    float halo = (1.0 - smoothstep(0.06, 0.5, distanceToCenter)) * 0.42;
    float horizontalRay = max(0.0, 1.0 - abs(point.y) * 22.0);
    horizontalRay *= horizontalRay;
    horizontalRay *= horizontalRay;
    horizontalRay *= 1.0 - smoothstep(0.04, 0.49, abs(point.x));
    float verticalRay = max(0.0, 1.0 - abs(point.x) * 22.0);
    verticalRay *= verticalRay;
    verticalRay *= verticalRay;
    verticalRay *= 1.0 - smoothstep(0.04, 0.49, abs(point.y));
    float alpha = (core + halo + (horizontalRay + verticalRay) * 0.11) * vOpacity;

    gl_FragColor = vec4(vColor * (1.0 + core * 0.48), alpha);
  }
`;

const NEBULA_VERTEX_SHADER = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const NEBULA_FRAGMENT_SHADER = `
  precision highp float;

  uniform float uTime;
  uniform float uOpacity;
  uniform float uAlphaCap;
  uniform float uSeed;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;

  varying vec2 vUv;

  float hash21(vec2 point) {
    point = fract(point * vec2(123.34, 456.21));
    point += dot(point, point + 45.32 + uSeed);
    return fract(point.x * point.y);
  }

  float noise21(vec2 point) {
    vec2 cell = floor(point);
    vec2 fraction = fract(point);
    fraction = fraction * fraction * (3.0 - 2.0 * fraction);

    return mix(
      mix(hash21(cell), hash21(cell + vec2(1.0, 0.0)), fraction.x),
      mix(hash21(cell + vec2(0.0, 1.0)), hash21(cell + vec2(1.0)), fraction.x),
      fraction.y
    );
  }

  vec3 nebulaField(vec2 point) {
    float softCloud = 0.0;
    float filament = 0.0;
    float lastOctave = 0.0;
    float amplitude = 0.54;
    mat2 turn = mat2(0.80, -0.60, 0.60, 0.80);

    for (int octave = 0; octave < NEBULA_OCTAVES; octave++) {
      float value = noise21(point);
      lastOctave = value;
      softCloud += value * amplitude;
      filament += (1.0 - abs(value * 2.0 - 1.0)) * amplitude;
      point = turn * point * 2.02 + vec2(13.7, 9.2);
      amplitude *= 0.49;
    }

    return vec3(softCloud, filament, lastOctave);
  }

  void main() {
    vec2 point = (vUv - 0.5) * vec2(1.72, 1.0);
    float time = uTime * 0.004;
    float lane = point.y - point.x * 0.34
      + 0.03 * sin(point.x * 5.0 + time * 0.3);
    float envelope = 1.0 - smoothstep(0.08, 0.39, abs(lane));
    envelope *= 1.0 - smoothstep(0.66, 0.98, abs(point.x));

    if (envelope <= 0.001) discard;

    float bend = noise21(point * 1.25 + vec2(time + uSeed, uSeed)) - 0.5;
    vec2 warp = vec2(bend, -bend * 0.72);
    vec3 field = nebulaField(point * 2.45 + warp * 1.15 + vec2(uSeed));
    float body = smoothstep(0.38, 0.73, field.x);
    float wisps = smoothstep(0.52, 0.80, field.y);
    float microDetail = 1.0 - abs(field.z * 2.0 - 1.0);
    float dust = 1.0 - smoothstep(0.20, 0.48, field.z);
    float density = envelope * (body * 0.77 + wisps * 0.30 + microDetail * 0.055);
    density *= 1.0 - dust * 0.32;
    density *= mix(0.58, 1.0, smoothstep(0.32, 0.67, vUv.x));

    vec3 color = mix(
      uColorA,
      uColorB,
      smoothstep(-0.55, 0.62, point.x + warp.x * 0.65)
    );
    float colorAccent = smoothstep(0.61, 0.92, field.y)
      * smoothstep(-0.10, 0.75, point.x);
    color = mix(color, uColorC, colorAccent * 0.34);
    color *= 0.78 + wisps * 0.34 + microDetail * 0.08;

    float alpha = min(uAlphaCap, density * uOpacity);
    if (alpha < 0.0025) discard;

    gl_FragColor = vec4(color, alpha);
  }
`;

function seededRandom(seed) {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function gaussian(random) {
  const a = Math.max(random(), 0.000001);
  const b = Math.max(random(), 0.000001);
  return Math.sqrt(-2 * Math.log(a)) * Math.cos(Math.PI * 2 * b);
}

function createDeepStarField(count) {
  const random = seededRandom(0x72616176);
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const opacities = new Float32Array(count);
  const phases = new Float32Array(count);
  const palette = [
    [0.82, 0.91, 1.0],
    [0.48, 0.84, 1.0],
    [0.74, 0.63, 1.0],
    [1.0, 0.94, 0.88],
    [0.28, 0.67, 1.0],
  ];

  for (let index = 0; index < count; index += 1) {
    const depth = 7 + random() * 38;
    const color = palette[Math.min(palette.length - 1, Math.floor(Math.pow(random(), 1.8) * palette.length))];
    const offset = index * 3;

    positions[offset] = (random() - 0.5) * depth * 1.52;
    positions[offset + 1] = (random() - 0.5) * depth * 0.88;
    positions[offset + 2] = 7.5 - depth;
    colors[offset] = color[0];
    colors[offset + 1] = color[1];
    colors[offset + 2] = color[2];
    sizes[index] = 0.28 + Math.pow(random(), 5.4) * 1.18;
    opacities[index] = 0.28 + Math.pow(random(), 0.72) * 0.7;
    phases[index] = random() * Math.PI * 2;
  }

  return { positions, colors, sizes, opacities, phases };
}

function createMilkyWay(count) {
  const random = seededRandom(0x6d696c6b);
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const opacities = new Float32Array(count);
  const phases = new Float32Array(count);
  const palette = [
    [0.78, 0.88, 1.0],
    [0.34, 0.75, 1.0],
    [0.64, 0.48, 1.0],
    [0.96, 0.96, 1.0],
    [0.25, 0.62, 1.0],
  ];

  for (let index = 0; index < count; index += 1) {
    const bulge = random() < 0.24;
    const radius = bulge
      ? Math.pow(random(), 2.45) * 5.2
      : Math.pow(random(), 1.18) * 18;
    const arm = index % 3;
    const armAngle = arm * ((Math.PI * 2) / 3) + radius * 0.39 + gaussian(random) * (bulge ? 0.78 : 0.3);
    const radialScatter = gaussian(random) * (bulge ? 0.72 : 0.34 + radius * 0.012);
    const effectiveRadius = Math.max(0, radius + radialScatter);
    const verticalScatter = gaussian(random) * (bulge ? 1.15 : 0.16 + radius * 0.028);
    const offset = index * 3;
    const paletteIndex = random() > 0.985 ? 4 : Math.floor(random() * 4);
    const color = palette[paletteIndex];
    const centerBrightness = 1 - Math.min(1, radius / 18);

    positions[offset] = Math.cos(armAngle) * effectiveRadius;
    positions[offset + 1] = Math.sin(armAngle) * effectiveRadius * 0.43 + gaussian(random) * 0.12;
    positions[offset + 2] = verticalScatter;
    colors[offset] = Math.min(1, color[0] + centerBrightness * 0.12);
    colors[offset + 1] = Math.min(1, color[1] + centerBrightness * 0.1);
    colors[offset + 2] = Math.min(1, color[2] + centerBrightness * 0.08);
    sizes[index] = 0.35 + Math.pow(random(), 2.9) * 1.5 + centerBrightness * 0.25;
    opacities[index] = 0.11 + Math.pow(random(), 1.35) * 0.53 + centerBrightness * 0.16;
    phases[index] = random() * Math.PI * 2;
  }

  return { positions, colors, sizes, opacities, phases };
}

function ParticleCloud({ field, materialRef }) {
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 1.25) },
  }), []);

  useFrame((state) => {
    if (!materialRef.current) return;
    materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    materialRef.current.uniforms.uPixelRatio.value = Math.min(state.gl.getPixelRatio(), 1.25);
  });

  return (
    <points frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[field.positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[field.colors, 3]} />
        <bufferAttribute attach="attributes-aSize" args={[field.sizes, 1]} />
        <bufferAttribute attach="attributes-aOpacity" args={[field.opacities, 1]} />
        <bufferAttribute attach="attributes-aPhase" args={[field.phases, 1]} />
      </bufferGeometry>
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={STAR_VERTEX_SHADER}
        fragmentShader={STAR_FRAGMENT_SHADER}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}

function NebulaLayer({
  alphaCap,
  colors,
  materialRef,
  octaves,
  opacity,
  renderOrder,
  seed,
  size,
}) {
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uOpacity: { value: opacity },
    uAlphaCap: { value: alphaCap },
    uSeed: { value: seed },
    uColorA: { value: new THREE.Vector3(...colors[0]) },
    uColorB: { value: new THREE.Vector3(...colors[1]) },
    uColorC: { value: new THREE.Vector3(...colors[2]) },
  }), [alphaCap, colors, opacity, seed]);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    }
  });

  return (
    <mesh frustumCulled={false} renderOrder={renderOrder}>
      <planeGeometry args={size} />
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={NEBULA_VERTEX_SHADER}
        fragmentShader={NEBULA_FRAGMENT_SHADER}
        defines={{ NEBULA_OCTAVES: octaves }}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.NormalBlending}
        toneMapped={false}
      />
    </mesh>
  );
}

function SpaceScene({ interaction, reducedMotion, quality }) {
  const stars = useRef();
  const galaxy = useRef();
  const farNebula = useRef();
  const nearNebula = useRef();
  const starMaterial = useRef();
  const galaxyMaterial = useRef();
  const farNebulaMaterial = useRef();
  const nearNebulaMaterial = useRef();
  const starField = useMemo(() => createDeepStarField(quality === "high" ? 6000 : 2600), [quality]);
  const milkyWay = useMemo(() => createMilkyWay(quality === "high" ? 11000 : 5000), [quality]);
  const farNebulaColors = useMemo(() => [
    [0.06, 0.23, 0.52],
    [0.29, 0.09, 0.49],
    [0.08, 0.34, 0.65],
  ], []);
  const nearNebulaColors = useMemo(() => [
    [0.035, 0.26, 0.42],
    [0.22, 0.10, 0.52],
    [0.05, 0.42, 0.62],
  ], []);

  useFrame((state, delta) => {
    if (!stars.current || !galaxy.current || !farNebula.current) return;
    const damping = 1 - Math.exp(-delta * 2.1);
    const time = reducedMotion ? 0 : state.clock.elapsedTime;
    const pointerX = THREE.MathUtils.clamp(interaction.current.x + interaction.current.tiltX * 0.62, -1, 1);
    const pointerY = THREE.MathUtils.clamp(interaction.current.y + interaction.current.tiltY * 0.52, -1, 1);
    const scroll = interaction.current.scroll;

    state.camera.position.x = THREE.MathUtils.lerp(state.camera.position.x, pointerX * 0.34, damping);
    state.camera.position.y = THREE.MathUtils.lerp(state.camera.position.y, -pointerY * 0.22 + (0.5 - scroll) * 0.16, damping);

    stars.current.rotation.x = THREE.MathUtils.lerp(stars.current.rotation.x, pointerY * 0.018 + Math.sin(time * 0.035) * 0.008, damping);
    stars.current.rotation.y = THREE.MathUtils.lerp(stars.current.rotation.y, -pointerX * 0.028 + time * 0.0014, damping);
    stars.current.position.x = THREE.MathUtils.lerp(stars.current.position.x, -pointerX * 0.22, damping);
    stars.current.position.y = THREE.MathUtils.lerp(stars.current.position.y, pointerY * 0.16 + (0.5 - scroll) * 0.22, damping);

    galaxy.current.rotation.x = THREE.MathUtils.lerp(galaxy.current.rotation.x, 1.11 + pointerY * 0.055, damping);
    galaxy.current.rotation.y = THREE.MathUtils.lerp(galaxy.current.rotation.y, -0.1 - pointerX * 0.045, damping);
    galaxy.current.rotation.z = THREE.MathUtils.lerp(galaxy.current.rotation.z, -0.34 + pointerX * 0.025 + Math.sin(time * 0.022) * 0.012, damping);
    galaxy.current.position.x = THREE.MathUtils.lerp(galaxy.current.position.x, 1.1 + pointerX * 0.58, damping);
    galaxy.current.position.y = THREE.MathUtils.lerp(galaxy.current.position.y, -0.1 - pointerY * 0.35 + (0.5 - scroll) * 0.44, damping);

    farNebula.current.position.x = THREE.MathUtils.lerp(farNebula.current.position.x, 0.8 + pointerX * 0.1, damping);
    farNebula.current.position.y = THREE.MathUtils.lerp(farNebula.current.position.y, -0.25 - pointerY * 0.08 + (0.5 - scroll) * 0.12, damping);
    farNebula.current.rotation.z = THREE.MathUtils.lerp(farNebula.current.rotation.z, pointerX * 0.004 + Math.sin(time * 0.012) * 0.004, damping);

    if (nearNebula.current) {
      nearNebula.current.position.x = THREE.MathUtils.lerp(nearNebula.current.position.x, 5.4 + pointerX * 0.3, damping);
      nearNebula.current.position.y = THREE.MathUtils.lerp(nearNebula.current.position.y, -1.0 - pointerY * 0.22 + (0.5 - scroll) * 0.2, damping);
      nearNebula.current.rotation.z = THREE.MathUtils.lerp(nearNebula.current.rotation.z, -0.12 + pointerX * 0.012, damping);
    }
  });

  return (
    <>
      <group ref={farNebula} position={[0.8, -0.25, -16.5]}>
        <NebulaLayer
          alphaCap={quality === "high" ? 0.16 : 0.115}
          colors={farNebulaColors}
          materialRef={farNebulaMaterial}
          octaves={quality === "high" ? 3 : 2}
          opacity={quality === "high" ? 0.22 : 0.16}
          renderOrder={-10}
          seed={0.73}
          size={[58, 32]}
        />
      </group>
      {quality === "high" && (
        <group ref={nearNebula} position={[5.4, -1.0, -8.5]} rotation={[0, 0, -0.12]}>
          <NebulaLayer
            alphaCap={0.055}
            colors={nearNebulaColors}
            materialRef={nearNebulaMaterial}
            octaves={2}
            opacity={0.075}
            renderOrder={-9}
            seed={4.37}
            size={[18, 10.5]}
          />
        </group>
      )}
      <group ref={stars}>
        <ParticleCloud field={starField} materialRef={starMaterial} />
      </group>
      <group ref={galaxy} position={[1.1, -0.1, -10.5]} rotation={[1.11, -0.1, -0.34]}>
        <ParticleCloud field={milkyWay} materialRef={galaxyMaterial} />
      </group>
    </>
  );
}

function useCosmicSettings(forceBalanced = false) {
  const [settings, setSettings] = useState(() => ({
    reducedMotion: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
    pageVisible: !document.hidden,
    quality: !forceBalanced && window.innerWidth > 980
      && (navigator.hardwareConcurrency || 8) >= 6
      && (navigator.deviceMemory || 4) >= 4
      && (navigator.maxTouchPoints || 0) === 0
      && !navigator.connection?.saveData
      ? "high"
      : "balanced",
  }));

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setSettings((current) => ({ ...current, reducedMotion: media.matches }));
    const updateVisibility = () => setSettings((current) => ({ ...current, pageVisible: !document.hidden }));
    media.addEventListener?.("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      media.removeEventListener?.("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  return forceBalanced ? { ...settings, quality: "balanced" } : settings;
}

function CosmicFrameDriver({ active, fps }) {
  const invalidate = useThree((state) => state.invalidate);

  useEffect(() => {
    if (!active) return undefined;
    let frame = 0;
    let previous = 0;
    const interval = 1000 / fps;
    const tick = (timestamp) => {
      if (timestamp - previous >= interval) {
        previous = timestamp - ((timestamp - previous) % interval);
        invalidate();
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [active, fps, invalidate]);

  return null;
}

function CosmicBackdropCanvas({ dashboardMode = false }) {
  const interaction = useRef({ x: 0, y: 0, tiltX: 0, tiltY: 0, scroll: 0 });
  const { pageVisible, reducedMotion, quality } = useCosmicSettings(dashboardMode);
  const veryLargeViewport = window.innerWidth * window.innerHeight > 3000000;
  const dpr = veryLargeViewport ? [1, 1] : quality === "high" ? [1, 1.2] : [1, 1];

  useEffect(() => {
    let frame = 0;
    let motionEnabled = isCosmicDeviceMotionEnabled();
    let calibration = null;
    const updateScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const maximum = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        interaction.current.scroll = Math.min(1, Math.max(0, window.scrollY / maximum));
        frame = 0;
      });
    };
    const updatePointer = (event) => {
      if (event.isPrimary === false) return;
      interaction.current.x = (event.clientX / Math.max(1, window.innerWidth)) * 2 - 1;
      interaction.current.y = (event.clientY / Math.max(1, window.innerHeight)) * 2 - 1;
    };
    const updateOrientation = (event) => {
      if (!motionEnabled || event.beta == null || event.gamma == null) return;
      if (!calibration) calibration = { beta: event.beta, gamma: event.gamma };
      const beta = THREE.MathUtils.clamp((event.beta - calibration.beta) / 34, -1, 1);
      const gamma = THREE.MathUtils.clamp((event.gamma - calibration.gamma) / 30, -1, 1);
      const angle = Number(window.screen?.orientation?.angle ?? window.orientation ?? 0);
      if (Math.abs(angle) === 90) {
        interaction.current.tiltX = angle > 0 ? beta : -beta;
        interaction.current.tiltY = angle > 0 ? -gamma : gamma;
      } else {
        interaction.current.tiltX = gamma;
        interaction.current.tiltY = beta;
      }
    };
    const updateMotionPreference = (event) => {
      motionEnabled = Boolean(event.detail?.enabled);
      calibration = null;
      if (!motionEnabled) {
        interaction.current.tiltX = 0;
        interaction.current.tiltY = 0;
      }
    };
    const resetPointer = () => {
      interaction.current.x = 0;
      interaction.current.y = 0;
    };

    updateScroll();
    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("pointermove", updatePointer, { passive: true });
    window.addEventListener("deviceorientation", updateOrientation, { passive: true });
    window.addEventListener(COSMIC_MOTION_EVENT, updateMotionPreference);
    window.addEventListener("blur", resetPointer);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateScroll);
      window.removeEventListener("pointermove", updatePointer);
      window.removeEventListener("deviceorientation", updateOrientation);
      window.removeEventListener(COSMIC_MOTION_EVENT, updateMotionPreference);
      window.removeEventListener("blur", resetPointer);
    };
  }, []);

  return (
    <div className="cosmic-backdrop" aria-hidden="true" data-testid="cosmic-backdrop">
      <Canvas
        camera={{ position: [0, 0, 7.5], fov: 56, near: 0.1, far: 80 }}
        dpr={dpr}
        frameloop="demand"
        gl={{
          alpha: true,
          antialias: false,
          depth: false,
          powerPreference: "high-performance",
          stencil: false,
        }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0);
          gl.outputColorSpace = THREE.SRGBColorSpace;
        }}
      >
        <CosmicFrameDriver active={!reducedMotion && pageVisible} fps={dashboardMode ? 30 : quality === "high" ? 48 : 30} />
        <SpaceScene interaction={interaction} reducedMotion={reducedMotion} quality={quality} />
      </Canvas>
    </div>
  );
}

class CosmicBackdropBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export default function CosmicBackdrop({ dashboardMode = false }) {
  return (
    <CosmicBackdropBoundary>
      <CosmicBackdropCanvas dashboardMode={dashboardMode} />
    </CosmicBackdropBoundary>
  );
}
